import { MessageSourceProps, withMessages } from 'react-message-source';
import * as React from 'react';
import { Component, ReactNode } from 'react';
import { Button, Container, Grid } from 'semantic-ui-react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { required } from 'utils/validatorUtils';
import Input from 'components/final-form/Input';
import styled from 'styled-components';
import { activateAccount, validateRegistrationKey } from 'services/accountServices';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PxGrid from 'components/layout/PxGrid';
import { UpsertAccountDto } from 'ts-types/api.types';
import LoaderComponent from 'components/LoaderComponent';
import { RouteComponentProps } from 'react-router';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';

const StyledGrid = styled(PxGrid)`
  padding: 1rem !important;
`;

const StyledButton = styled(Button)`
  margin: 1rem 0.5rem 0 0 !important;
`;

const ScrollableContainer = styled.div`
  height: 100%;
  overflow-y: auto;
`;

interface Errors {
  confirm?: string
}

interface Values {
  password: string,
  confirm: string
}

interface Props extends MessageSourceProps, RouteComponentProps<any>, NotificationSystemProps {

}

interface State {
  isActivated: boolean,
  account?: UpsertAccountDto,
  loadingMessage?: string
}


class Register extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      isActivated: false
    };
  }


  render(): ReactNode {
    const {isActivated, loadingMessage} = this.state;

    return (
        <ScrollableContainer>
          <Container text>
            {isActivated ? this.registrationForm() : this.activationKeyForm()}
            {loadingMessage ? <LoaderComponent message={loadingMessage}/> : ""}
          </Container>
        </ScrollableContainer>
    );
  }

  activationKeyForm = () => {
    return (
        <FinalForm
            onSubmit={this.submitActivationKey}
            render={this.renderActivationForm}
        />
    );
  };

  registrationForm = () => {
    const {account} = this.state;
    return (
        <FinalForm
            onSubmit={this.submitRegistration}
            render={this.renderRegistrationForm}
            subscription={{pristine: true}}
            initialValues={account}

            validate={(values: Partial<Values>) => {
              const errors: Errors = {};
              if (values.password !== values.confirm) {
                errors.confirm = "Must match";
              }
              return errors;
            }}
        />
    );
  };

  renderRegistrationForm = ({handleSubmit, submitting}: FormRenderProps) => {
    const {getMessage} = this.props;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <StyledGrid>
                <Grid.Row centered>
                  <Grid.Column width={12} verticalAlign="middle" textAlign="left">
                    {getMessage("account.activationKey")}
                    <Field
                        fluid
                        name="regKey"
                        component={Input}
                        validate={required}
                        disabled
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row centered>
                  <Grid.Column width={6} verticalAlign="middle" textAlign="left">
                    {getMessage("account.firstName")}
                    <Field
                        fluid
                        name="firstName"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                  <Grid.Column width={6} verticalAlign="middle" textAlign="left">
                    {getMessage("account.lastName")}
                    <Field
                        fluid
                        name="lastName"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row centered>
                  <Grid.Column width={12} verticalAlign="middle" textAlign="left">
                    {getMessage("account.email")}
                    <Field
                        fluid
                        name="email"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row centered>
                  <Grid.Column width={12} verticalAlign="middle" textAlign="left">
                    {getMessage("account.username")}
                    <Field
                        fluid
                        name="username"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row centered>
                  <Grid.Column width={12} verticalAlign="middle" textAlign="left">
                    {getMessage("account.password")}
                    <Field
                        fluid
                        name="password"
                        type="password"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row centered>
                  <Grid.Column width={12} verticalAlign="middle" textAlign="left">
                    {getMessage("account.confirm")}
                    <Field
                        fluid
                        name="confirm"
                        type="password"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row centered>
                  <Grid.Column width={12} verticalAlign="middle" textAlign="left">
                    <Button compact type="submit" primary
                            disabled={submitting}>{getMessage("account.activate")}</Button>
                    <StyledButton compact disabled={submitting}
                                  onClick={this.cancelRegistration}>{getMessage("account.cancel")}</StyledButton>
                  </Grid.Column>
                </Grid.Row>
              </StyledGrid>
            </FormContent>
          </FormContainer>
        </form>
    )

  };

  renderActivationForm = ({handleSubmit, submitting}: FormRenderProps) => {
    const {getMessage} = this.props;

    return (
        <form onSubmit={handleSubmit}>
          <StyledGrid>
            <Grid.Row centered>
              <Grid.Column width={8} verticalAlign="middle" textAlign="left">
                {getMessage("account.activationKey")}
                <Field
                    fluid
                    name="regKey"
                    component={Input}
                    validate={required}
                    autoFocus
                />

                <StyledButton compact type="submit" primary
                              disabled={submitting}>{getMessage("account.validate")}</StyledButton>
                <StyledButton compact disabled={submitting}
                              onClick={this.cancelRegistration}>{getMessage("account.cancel")}</StyledButton>
              </Grid.Column>
            </Grid.Row>
          </StyledGrid>
        </form>
    )
  };

  cancelRegistration = () => {
    const {history} = this.props;
    history.push("/");
  };

  submitActivationKey = ({regKey}: any) => {
    const {getMessage} = this.props;

    this.setState({loadingMessage: getMessage("account.validation.loading")});

    validateRegistrationKey(regKey)
        .then(account => {
          this.setState({
            account: account,
            isActivated: true,
            loadingMessage: undefined
          })
        })
        .catch(error => {
          this.setState({
            loadingMessage: undefined
          })
        });
  };

  submitRegistration = (values: Partial<UpsertAccountDto>) => {
    const {getMessage, history, selectionNotificationSystem} = this.props;

    this.setState({loadingMessage: getMessage("account.activation.loading")});

    activateAccount(values)
        .then(() => {
          // @ts-ignore
          const notification = selectionNotificationSystem.current;
          if (notification) {
            notification.addNotification({
              title: getMessage("account.activation.success"),
              message: getMessage("account.activation.successful.message"),
              level: 'success'
            });
          }
          history.push("/");
        })
        .catch(error => {
          this.setState({
            loadingMessage: undefined
          })
        });
  };

}

export default withNotificationSystemContext(withMessages(Register));
